import React, { useEffect, useState } from "react";
import { ThemeService } from "../../services/themeService";

export const ThemeContext =
  React.createContext<ReturnType<typeof useThemeContextHook> | undefined>(
    undefined
  );

const useThemeContextHook = () => {
  const themeService = ThemeService.getInstance();
  // For testing: uncomment the next line to simulate Domo domain
  // const isDomo = true;
  const isDomo = window.location.hostname.includes("domo");
  const defaultMode = isDomo ? "dark" : "light";

  // Initialize with the correct theme immediately to prevent flickering
  const getInitialTheme = () => {
    // Logic: Use localStorage first, then system preference, then default for domain
    return themeService.getEffectiveThemeForDomain(isDomo);
  };

  const [mode, setMode] = useState(getInitialTheme);

  const updateMode = (mode: string) => {
    setMode(mode);
    console.log("Updating theme to:", mode);

    // Always save to localStorage for persistence
    themeService.setStoredTheme(mode);
  };

  // Listen for system theme changes
  useEffect(() => {
    if (isDomo) {
      console.log("DOMO DOMAIN: Ignoring system theme changes, staying dark");
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if no theme is stored in localStorage
      if (!themeService.getStoredTheme()) {
        const newTheme = e.matches ? "dark" : "light";
        console.log("System theme changed to:", newTheme);
        setMode(newTheme);
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [isDomo, themeService]);

  return { mode, setMode: updateMode };
};

export const ThemeContextProvider = ({
  children,
}: React.PropsWithChildren<any>) => {
  const { mode, setMode } = useThemeContextHook();
  return (
    <ThemeContext.Provider value={{ mode, setMode }}>
      <div className="App" data-theme={mode} id="app">
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = React.useContext(ThemeContext);
  if (typeof context === "undefined") {
    throw new Error(
      "theme context can only be accessed from within it's provider"
    );
  }
  return context;
};
